import { useState, useEffect, useCallback } from 'react';
import { ActualServer, BudgetMonthData } from '../types';
import { fetchBudgetMonth } from '../utils/budgetApi';
import {
  adjacentAvailableMonth,
  canNavigateMonth,
  getCurrentMonth,
  shiftMonth
} from '../utils/budgetFormat';
import { patchCategoryBudgetInMonth } from '../utils/budgetPatch';

interface UseBudgetMonthOptions {
  server: ActualServer | null;
  month?: string;
  enabled?: boolean;
}

export function useBudgetMonth({ server, month, enabled = true }: UseBudgetMonthOptions) {
  const [selectedMonth, setSelectedMonthState] = useState(month ?? getCurrentMonth());
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [budgetMonth, setBudgetMonth] = useState<BudgetMonthData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (month) {
      setSelectedMonthState(month);
    }
  }, [month]);

  const setSelectedMonth = useCallback((nextMonth: string) => {
    setSelectedMonthState(nextMonth);
  }, []);

  const hasMonthBounds = availableMonths.length > 0;

  const goToAdjacentMonth = useCallback((delta: -1 | 1) => {
    setSelectedMonthState(current => {
      if (hasMonthBounds) {
        return adjacentAvailableMonth(availableMonths, current, delta) ?? current;
      }
      return shiftMonth(current, delta);
    });
  }, [availableMonths, hasMonthBounds]);

  const loadBudgetMonth = useCallback(async () => {
    if (!server?.defaultBudgetId || !enabled) {
      setBudgetMonth(null);
      setAvailableMonths([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchBudgetMonth(server, selectedMonth);
      setBudgetMonth(result.budgetMonth);
      setAvailableMonths(result.availableMonths);
      if (result.month && result.month !== selectedMonth) {
        setSelectedMonthState(result.month);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'REQUEST_FAILED';
      setError(message);
      setBudgetMonth(null);
    } finally {
      setLoading(false);
    }
  }, [server, selectedMonth, enabled]);

  useEffect(() => {
    loadBudgetMonth();
  }, [loadBudgetMonth]);

  const patchCategoryBudget = useCallback((categoryId: string, amountCents: number) => {
    setBudgetMonth(prev => {
      if (!prev) return prev;
      return patchCategoryBudgetInMonth(prev, categoryId, amountCents);
    });
  }, []);

  const replaceBudgetMonth = useCallback((data: BudgetMonthData) => {
    setBudgetMonth(data);
  }, []);

  return {
    selectedMonth,
    setSelectedMonth,
    goToAdjacentMonth,
    canGoPrevious: hasMonthBounds
      ? canNavigateMonth(availableMonths, selectedMonth, -1)
      : true,
    canGoNext: hasMonthBounds
      ? canNavigateMonth(availableMonths, selectedMonth, 1)
      : true,
    availableMonths,
    budgetMonth,
    loading,
    error,
    refresh: loadBudgetMonth,
    patchCategoryBudget,
    replaceBudgetMonth
  };
}
